import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { index, integer, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { numericAsNumber } from '../../../custom-type'
import { user } from '../../auth'
import { Challenge, LessonModule } from '../../content'
import { Course } from '../course'
import { LearnEnrollment } from '../learn-enrollment'

// =============================================================================
// LESSON HISTORY DAILY - Lookup table (closed UTC days; today is computed live)
// Stores one row per (userId, learnEnrollmentId, date) for lesson history list.
// Type: CHALLENGE | LESSON | PRACTICE (Tantangan | Tugas | Mandiri).
//
// DEBUG NOTES:
// - Formula `computeLessonHistoryDaily` (packages/trigger/src/metrics/), stored for
//   closed days by `rollupUsers`. One row per enrollment, on the UTC day of its CURRENT
//   `LearnEnrollment.updatedAt` — every write to the enrollment moves the row to today.
//   This table is a derived cache, not a source of truth.
// - The store deletes the (users, range) slice before inserting, so a duplicate-key
//   error on the unique (userId, learnEnrollmentId, date) tuple below means the compute
//   emitted two rows for one enrollment and day.
// - Dashboard "Tugas / Tantangan / Latihan" counts are derived by counting
//   DISTINCT `learnEnrollmentId` filtered by `type` over a date range
//   (LESSON->Tugas, CHALLENGE->Tantangan, PRACTICE->Mandiri/Latihan). Wrong
//   counts usually mean wrong `type` tagging or duplicate enrollment rows, not
//   a bad COUNT query.
// =============================================================================

export const LessonHistoryDaily = pgTable(
  'lesson_history_daily',
  {
    id: uuid().defaultRandom().primaryKey(), // Surrogate PK; row identity is logically the (userId, learnEnrollmentId, date) tuple below
    // Owner of the history row. onDelete: 'cascade' — if the user is deleted, their history rows go too.
    userId: text()
      .references(() => user.id, { onDelete: 'cascade' })
      .notNull(),
    // The enrollment this activity belongs to. onDelete: 'cascade' — history is meaningless without its enrollment.
    // This is also the column the dashboard counts DISTINCT (per `type`) to get Tugas/Tantangan/Latihan totals.
    learnEnrollmentId: uuid()
      .references(() => LearnEnrollment.id, { onDelete: 'cascade' })
      .notNull(),
    // The specific activity day, normalized to START-OF-DAY UTC (00:00:00Z). withTimezone stores tz-aware.
    // DEBUG: if grouping produces unexpected duplicates, verify the compute truncated time to 00:00 UTC
    // (not local midnight) before writing — non-normalized timestamps break the unique constraint dedupe.
    date: timestamp({ mode: 'date', withTimezone: true }).notNull(), // The specific date (normalized to start of day)

    // Denormalized from enrollment + course/challenge/lessonModule (set null if source is deleted so history row remains)
    // WHY onDelete: 'set null' (NOT cascade): these point at editable CONTENT. If a course/challenge/module is
    // deleted or republished and the old id disappears, the learner's history must SURVIVE — we just lose the
    // backlink. DEBUG: a NULL courseId/challengeId/lessonModuleId here means the source content was deleted,
    // not that the job failed. The human-readable lessonModuleTitle below is the snapshot fallback for display.
    courseId: uuid().references(() => Course.id, { onDelete: 'set null' }), // FK -> Course; null if course deleted
    challengeId: uuid().references(() => Challenge.id, { onDelete: 'set null' }), // FK -> Challenge; null if challenge deleted
    lessonModuleId: uuid().references(() => LessonModule.id, { onDelete: 'set null' }), // FK -> LessonModule; null if module deleted
    lessonModuleTitle: text().notNull(), // Snapshot of the module title at calc time; persists even after lessonModuleId nulls out
    gradeLevel: text(), // Optional grade label (e.g. class/level) snapshot; nullable when not applicable

    startedAt: timestamp({ mode: 'date', withTimezone: true }), // When the learner first started this activity; null if not yet started
    completedLessons: integer().notNull().default(0), // Whole-enrollment count of completed lessons (status = 'COMPLETED' across all the enrollment's LearnProgress rows, NOT scoped to `date`); numerator
    totalLessons: integer().notNull().default(0), // Total lessons in this enrollment (denominator); progress = completed/total
    // Activity kind. Enum values + Indonesian UI labels: CHALLENGE->Tantangan, LESSON->Tugas, PRACTICE->Mandiri.
    // DEBUG: dashboard category counts filter on THIS column — a mis-tagged row lands in the wrong tab's total.
    type: text({ enum: ['CHALLENGE', 'LESSON', 'PRACTICE'] }).notNull(),
    score: numericAsNumber(), // Earned/possible XP ratio ×100 (sum(EarnedXp.xpEarned)/sum(LessonToSkillWeight.value)*100), clamped to [0,100] by the writer — 0–100 is a writer convention, NOT a DB CHECK (column is numeric(12,2)). NULL = no gradable lessons / no possible XP (don't read as 0)

    // --- Membership snapshot (the "Laporan" dimension) --------------------------------------
    // The school context the learner was in AT ROLL-UP TIME, stamped by the producer on every write.
    // A report ("Laporan baca kelas 1 A") groups on these, so it keeps meaning the members of 1 A in
    // that period even after a student is moved to another class or promoted to the next grade.
    // Every column is nullable: a child who only belongs to a family organization has no school
    // context. A child can be a member of two organizations at once (family + school), which is
    // exactly why the organization is recorded here instead of being re-derived at read time.
    // NOT FOREIGN KEYS, deliberately. A snapshot must carry no referential dependency at all: even
    // `ON DELETE SET NULL` erases the value the moment a school or a class is deleted, which is the
    // history loss the snapshot exists to prevent. Plain text, resolved by the producer via
    // resolveMetricSnapshotContexts (packages/drizzle/src/queries/metric-snapshot-context.ts).
    // IDS ONLY — no name/avatar columns on this table. Its grain multiplies (user, day) by
    // an ENROLLMENT, so repeating a name and two URLs on every row would bloat one of the biggest
    // tables in the set for nothing: a Laporan aggregates from the (user, day)-grain tables, which
    // do carry the labels, and these ids are still enough to filter by school/class and to join
    // back to those rows.
    schoolOrganizationId: text(),
    // The "Kelas" / rombel. Always a team of schoolOrganizationId above.
    schoolTeamId: text(),
    // The "Tingkat" / grade, mirroring user.schoolClass — a plain text column, not a DB enum.
    // No separate label: it already IS the human-readable value, not an id.
    schoolClass: text(),

    // Tracking fields
    calculatedAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(), // Last time the trigger.dev job recomputed this row; stale value => job not re-running
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(), // Row first inserted (DB insert time, NOT the activity `date`)
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`), // Auto-bumped to now() on UPDATE; null until first update happens
  },
  (t) => [
    index('idx_lesson_history_daily_user').on(t.userId), // Speeds "all history for a user" lookups
    index('idx_lesson_history_daily_date').on(t.date), // Speeds date-range scans (dashboard windows)
    index('idx_lesson_history_daily_user_date').on(t.userId, t.date), // Composite for per-user date-range queries (the common dashboard read path)
    index('idx_lesson_history_daily_lesson_module_title').on(t.lessonModuleTitle), // Supports title search/sort in the history list UI
    // Reporting reads: every row a school produced in a period, then narrowed to one class.
    index('idx_lesson_history_daily_school_org_date').on(t.schoolOrganizationId, t.date),
    index('idx_lesson_history_daily_school_team_date').on(t.schoolTeamId, t.date),
    // THE GRAIN: one row per (user, enrollment, day). The roll-up deletes the (users, range) slice and
    // re-inserts it. DEBUG: a duplicate-key violation means the compute emitted two rows for one tuple,
    // or `date` was not start-of-day-UTC normalized.
    unique('uq_lesson_history_daily_user_enrollment_date').on(t.userId, t.learnEnrollmentId, t.date),
  ],
)

// Drizzle relations for query-time joins. NOTE: course/challenge/lessonModule relations can resolve to
// undefined at runtime when their *Id column is NULL (source content deleted) — always null-check in callers.
export const lessonHistoryDailyRelations = relations(LessonHistoryDaily, ({ one }) => ({
  user: one(user, {
    fields: [LessonHistoryDaily.userId],
    references: [user.id],
  }),
  learnEnrollment: one(LearnEnrollment, {
    fields: [LessonHistoryDaily.learnEnrollmentId],
    references: [LearnEnrollment.id],
  }),
  course: one(Course, {
    fields: [LessonHistoryDaily.courseId],
    references: [Course.id],
  }),
  challenge: one(Challenge, {
    fields: [LessonHistoryDaily.challengeId],
    references: [Challenge.id],
  }),
  lessonModule: one(LessonModule, {
    fields: [LessonHistoryDaily.lessonModuleId],
    references: [LessonModule.id],
  }),
}))

// Zod schema for INSERT payloads (used by the job when writing rows).
// Omits id (DB-generated UUID) + createdAt/updatedAt (DB-managed). calculatedAt is KEPT so the job can set it.
export const InsertLessonHistoryDailySchema = createInsertSchema(LessonHistoryDaily).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

// Zod schema for UPDATE payloads. All fields optional (.partial()).
// Omits the upsert identity columns (userId, learnEnrollmentId, date) so updates can't move a row to a
// different conflict key, plus id + DB-managed timestamps. To re-key a row, delete + insert instead.
export const UpdateLessonHistoryDailySchema = createUpdateSchema(LessonHistoryDaily).partial().omit({
  id: true,
  userId: true,
  learnEnrollmentId: true,
  date: true,
  createdAt: true,
  updatedAt: true,
})

// Zod schema for SELECT rows (full row shape, no omits) — use for validating reads/serialized output.
export const SelectLessonHistoryDailySchema = createSelectSchema(LessonHistoryDaily)

export type InsertLessonHistoryDaily = typeof LessonHistoryDaily.$inferInsert
export type UpdateLessonHistoryDaily = z.infer<typeof UpdateLessonHistoryDailySchema>
export type SelectLessonHistoryDaily = typeof LessonHistoryDaily.$inferSelect
